import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import InvoiceDownload from "../components/InvoiceDownload";
import "./Checkout.css";

function Success() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const orderId = searchParams.get("order");
  const token = searchParams.get("token");
  const isFreeProduct = searchParams.get("free") === "true";
  const productSlug = searchParams.get("product");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(!isFreeProduct);
  const [error, setError] = useState("");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(isFreeProduct);

  const fetchOrder = useCallback(async () => {
    // Skip order verification for free products
    if (isFreeProduct) {
      setLoading(false);
      return;
    }

    if (!orderId) {
      setError("No order ID provided.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `/api/order?order_id=${encodeURIComponent(orderId)}&token=${encodeURIComponent(token || "")}`,
      );
      const data = await res.json();

      if (!res.ok || data.error) {
        setError(data.error || "Order not found.");
        setLoading(false);
        return;
      }

      setOrder(data);
      if (
        data.status === "paid" ||
        data.status === "expired" ||
        data.status === "cancelled"
      ) {
        setLoading(false);
      }
    } catch {
      setError("Failed to fetch order.");
      setLoading(false);
    }
  }, [orderId, token, isFreeProduct]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Poll every 3 seconds while pending
  useEffect(() => {
    if (!order || order.status !== "pending") return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(
          `/api/order?order_id=${encodeURIComponent(orderId)}&token=${encodeURIComponent(token || "")}`,
        );
        const data = await res.json();
        if (res.ok && data && data.status !== "pending") {
          setOrder(data);
          setLoading(false);
        }
      } catch {
        // retry next interval
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [order, orderId, token]);

  // Check authentication for free products
  useEffect(() => {
    if (isFreeProduct && !authLoading && !user) {
      // Redirect to auth page if not signed in
      navigate("/auth", {
        state: {
          returnTo: `/success?product=${productSlug}&free=true`,
          message: "Please sign in to download free tools.",
        },
      });
    }
  }, [isFreeProduct, authLoading, user, navigate, productSlug]);

  // Generate download URL when order is paid OR immediately for free products
  useEffect(() => {
    if (isFreeProduct && !downloadUrl && productSlug) {
      const generateDownload = async () => {
        setDownloadLoading(true);
        try {
          const res = await fetch("/api/generate-download-url", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              product_slug: productSlug,
              is_free: true,
            }),
          });
          const data = await res.json();
          if (res.ok && data.download_url) {
            setDownloadUrl(data.download_url);
          } else {
            console.error("Failed to generate download URL:", data.error);
            setError(data.error || "Failed to generate download URL");
          }
        } catch (err) {
          console.error("Error generating download URL:", err);
          setError("Failed to generate download URL");
        } finally {
          setDownloadLoading(false);
        }
      };
      generateDownload();
    } else if (order && order.status === "paid" && !downloadUrl) {
      const generateDownload = async () => {
        setDownloadLoading(true);
        try {
          const res = await fetch("/api/generate-download-url", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order_id: order.order_id }),
          });
          const data = await res.json();
          if (res.ok && data.download_url) {
            setDownloadUrl(data.download_url);
          } else {
            console.error("Failed to generate download URL:", data.error);
          }
        } catch (err) {
          console.error("Error generating download URL:", err);
        } finally {
          setDownloadLoading(false);
        }
      };
      generateDownload();
    }
  }, [order, downloadUrl, isFreeProduct, productSlug]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <>
        <Helmet>
          <title>Checking Authentication | AxiomLabs Solutions</title>
          <meta
            name="description"
            content="Verifying your account to access your purchase."
          />
        </Helmet>
        <div className="checkout-page checkout-page--loading">
          <div className="checkout">
            <div className="checkout__spinner">
              <div className="spinner-ring" />
            </div>
            <h1 className="checkout__title">Checking Authentication</h1>
          </div>
        </div>
      </>
    );
  }

  // Free product download page (requires auth)
  if (isFreeProduct && user) {
    return (
      <>
        <Helmet>
          <title>Download Ready | AxiomLabs Solutions</title>
          <meta
            name="description"
            content="Your free download from AxiomLabs Solutions is ready."
          />
        </Helmet>
        <div className="checkout-page">
          <div className="checkout checkout--success">
            <div className="checkout__icon checkout__icon--success">✓</div>
            <h1 className="checkout__title">Download Ready</h1>
            <p className="checkout__text">
              Your free download is ready. Sign in required for all downloads.
            </p>

            {downloadLoading && (
              <div className="checkout__download-loading">
                <div className="spinner-ring" />
                <p>Preparing your download...</p>
              </div>
            )}

            {downloadUrl && !downloadLoading && (
              <>
                <a
                  href={downloadUrl}
                  className="checkout__btn checkout__btn--primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Now
                </a>
                <div className="checkout__download-notice">
                  <p>
                    This download link is valid for 24 hours. If you need it
                    again, just visit the product page and download it again.
                  </p>
                </div>
              </>
            )}

            {error && (
              <div className="checkout__error">
                <p>{error}</p>
              </div>
            )}

            <Link
              to="/products"
              className="checkout__btn checkout__btn--secondary"
            >
              Browse More Products
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Paid product flow continues below
  if (error) {
    return (
      <>
        <Helmet>
          <title>Order Not Found | AxiomLabs Solutions</title>
          <meta
            name="description"
            content="We could not find your order. Please contact support for assistance."
          />
        </Helmet>
        <div className="checkout-page">
          <div className="checkout checkout--cancelled">
            <div className="checkout__icon checkout__icon--cancelled">✕</div>
            <h1 className="checkout__title">Order Not Found</h1>
            <p className="checkout__text">{error}</p>
            <Link to="/products" className="checkout__btn">
              Browse Products
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Loading state
  if (loading || (order && order.status === "pending")) {
    return (
      <>
        <Helmet>
          <title>Confirming Payment | AxiomLabs Solutions</title>
          <meta
            name="description"
            content="Waiting for blockchain confirmation of your payment. This usually takes 1-5 minutes."
          />
        </Helmet>
        <div className="checkout-page checkout-page--loading">
          <div className="checkout">
            <div className="checkout__spinner">
              <div className="spinner-ring" />
            </div>
            <h1 className="checkout__title">Confirming Payment</h1>
            <p className="checkout__text">
              Waiting for blockchain confirmation. This usually takes 1-5
              minutes.
            </p>
            {order && (
              <p className="checkout__order-id">Order: {order.order_id}</p>
            )}
          </div>
        </div>
      </>
    );
  }

  // Paid state
  if (order && order.status === "paid") {
    const paidDate = order.paid_at
      ? new Date(order.paid_at).toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : new Date().toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
          year: "numeric",
        });

    return (
      <>
        <Helmet>
          <title>Payment Confirmed | AxiomLabs Solutions</title>
          <meta
            name="description"
            content="Your purchase from AxiomLabs Solutions is complete. Thank you for your order."
          />
        </Helmet>
        <div className="checkout-page">
          <div className="checkout checkout--success">
            <div className="checkout__icon checkout__icon--success">✓</div>
            <h1 className="checkout__title">Payment Confirmed</h1>
            <p className="checkout__text">
              Your purchase is complete. Thank you for your order.
            </p>

            <div className="checkout__details">
              <div className="checkout__detail-row">
                <span>Order</span>
                <span className="checkout__detail-value">{order.order_id}</span>
              </div>
              <div className="checkout__detail-row">
                <span>Product</span>
                <span className="checkout__detail-value">
                  {order.product_name}
                </span>
              </div>
              {order.license_key && (
                <div className="checkout__detail-row">
                  <span>License Key</span>
                  <span className="checkout__detail-value checkout__detail-value--license">
                    {order.license_key}
                  </span>
                </div>
              )}
              <div className="checkout__detail-row">
                <span>Date</span>
                <span className="checkout__detail-value">{paidDate}</span>
              </div>
              <div className="checkout__detail-row">
                <span>Status</span>
                <span className="checkout__detail-value checkout__detail-value--paid">
                  PAID
                </span>
              </div>
            </div>

            <InvoiceDownload
              order_id={order.order_id}
              product_name={order.product_name}
              license_key={order.license_key}
              amount_eur={order.amount}
              paid_at={paidDate}
              customer_email={order.customer_email}
            />

            {/* Download button for paid products */}
            {downloadLoading && (
              <div className="checkout__download-loading">
                <div className="spinner-ring" />
                <p>Preparing your download...</p>
              </div>
            )}
            {downloadUrl && !downloadLoading && (
              <a
                href={downloadUrl}
                className="checkout__btn checkout__btn--primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download Your Product
              </a>
            )}
            {!downloadUrl && !downloadLoading && order.product_slug && (
              <div className="checkout__download-notice">
                <p>
                  Your download will be available shortly. If it doesn't appear,
                  please contact support with your order ID.
                </p>
              </div>
            )}

            <Link
              to="/products"
              className="checkout__btn checkout__btn--secondary"
            >
              Browse More Products
            </Link>

            <div className="checkout__powered">
              <span>Secured by</span>
              <strong>NOWPayments</strong>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Expired or cancelled
  return (
    <>
      <Helmet>
        <title>Payment Incomplete | AxiomLabs Solutions</title>
        <meta
          name="description"
          content={
            order?.status === "expired"
              ? "Payment window expired. No charges were made."
              : "Transaction was cancelled."
          }
        />
      </Helmet>
      <div className="checkout-page">
        <div className="checkout checkout--cancelled">
          <div className="checkout__icon checkout__icon--cancelled">✕</div>
          <h1 className="checkout__title">Payment Incomplete</h1>
          <p className="checkout__text">
            {order?.status === "expired"
              ? "Payment window expired. No charges were made."
              : "Transaction was cancelled."}
          </p>
          <Link to="/products" className="checkout__btn">
            Browse Products
          </Link>
        </div>
      </div>
    </>
  );
}

export default Success;
